import { Component, Injector, OnInit } from '@angular/core';
import {
    CompaniesProvider,
    IPersonForm,
    onCreatePersonSuccessfully,
    onDeletePersonSuccessfully,
    onUpdatePersonSuccessfully,
    PersonsProvider,
    TagsProvider,
} from '@modeso/aumico-lib-companies-fe';
import { IPageHeader } from '@modeso/aumico-lib-core-fe';
import { OrganizationProvider } from '@modeso/aumico-lib-users-fe';
import { CPMMState } from '@modeso/aumico-lib-users-fe/enums/cpmm-state.enum';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { IPersonDTO, ITagDTO, ITagUsageDTO } from '@modeso/types__aumico-lib-companies-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter } from 'rxjs';
import { Paths } from '../../../enums/paths.enum';
import { RouteLabels } from '../../../enums/routeLabels.enum';
import { BasePageComponent } from '../../../pages/base.page';

/**
 * REV-22155 — "+ Neue Person" (route `add`) and the "Profil" tab (route `:personId/profile`), like
 * `AddOrEditCompanyPage` / `…Page2` in one page: it loads what the form needs, converts
 * `IPersonDTO` ↔ `IPersonForm`, saves, deletes, (de)activates and routes the tag events.
 */
@UntilDestroy()
@Component({
    selector: 'app-add-edit-person-page',
    templateUrl: './add-edit-person.page.html',
})
export class AddEditPersonPage extends BasePageComponent implements OnInit {
    readonly pageHeader: IPageHeader = {
        isLocalizedKey: true,
        value: RouteLabels.PERSON_NEW,
    };

    personId: string | null = null;
    person: IPersonForm | null = null;
    active = true;
    saving = false;
    cpmmActive = false;

    users: INameId[] = [];
    companies: { _id: string; name: string }[] = [];
    tags: ITagDTO[] = [];
    tagUsage: { id: string; usage: ITagUsageDTO } | null = null;
    tagError: unknown = null;

    constructor(
        injector: Injector,
        private readonly personsProvider: PersonsProvider,
        private readonly tagsProvider: TagsProvider,
        private readonly companiesProvider: CompaniesProvider,
        private readonly organizationProvider: OrganizationProvider,
    ) {
        super(injector);
    }

    get isNew(): boolean {
        return this.personId == null;
    }

    override ngOnInit(): void {
        super.ngOnInit();

        // On the profile the id sits on the parent route (`:personId/profile`); `add` has none.
        this.personId = this.activatedRoute.snapshot.parent?.params['personId'] ?? null;

        this.organizationProvider
            .getPossibleAssignees()
            .pipe(untilDestroyed(this))
            .subscribe((users) => {
                this.users = users.map((user) => ({ id: user.id, name: `${user.firstName} ${user.lastName}` }));
            });

        // Lean company list (id + name) for "Verknüpfte Firmen".
        this.companiesProvider
            .getCompaniesNameIdLegalForm$()
            .pipe(untilDestroyed(this))
            .subscribe((companies) => {
                this.companies = (companies ?? []).map((company) => ({ _id: company._id, name: company.name }));
            });

        this.organizationProvider
            .getCurrentOrganization$()
            .pipe(
                filter((organization) => organization != null),
                untilDestroyed(this),
            )
            .subscribe((organization) => {
                this.cpmmActive = organization.cpmmActivationDetails?.activationState === CPMMState.ACTIVE;
            });

        this.tagsProvider
            .getTags$()
            .pipe(untilDestroyed(this))
            .subscribe((tags) => (this.tags = tags ?? []));

        this.tagsProvider
            .getTagUsage$()
            .pipe(untilDestroyed(this))
            .subscribe((usage) => (this.tagUsage = usage));

        if (!this.isNew) {
            this.personsProvider
                .getSelectedPerson$()
                .pipe(
                    filter((person) => person != null && person._id === this.personId),
                    untilDestroyed(this),
                )
                .subscribe((person) => {
                    this.active = person.active;
                    this.person = this.toForm(person);
                });
        }

        this.listenToSaveAndDelete();
        this.showErrors();

        this.companiesProvider.dispatchCompaniesNameIdLegalFormList();
        this.tagsProvider.dispatchTagsList();
        this.organizationProvider.dispatchGetCurrentOrganization();
        // Mandatsleiter / Sachbearbeiter: load the users here too, like the company profile — else
        // the names stay empty after opening the profile directly (F5, deep link).
        this.organizationProvider.dispatchGetUsers();
        // The detail page already loads the person; `add` needs nothing.
    }


    /**
     * Failures of status changes, bulk actions and tag calls only landed in the store and stayed
     * silent. Shown once, then cleared so the same error is not reported again.
     */
    private showErrors(): void {
        this.personsProvider
            .getPersonsError$()
            .pipe(
                filter((error) => error != null),
                untilDestroyed(this),
            )
            .subscribe((error) => {
                // 409 = the person still has projects (only the delete answers with it), as for companies.
                if (error.status === 409) {
                    this.fireErrorMessage(
                        'aumico_person_delete_failed_has_projects_message',
                        'aumico_person_delete_failed_title',
                    );
                } else {
                    this.fireErrorMessage();
                }
                this.personsProvider.resetPersonsErrors();
            });

        this.tagsProvider
            .getTagsError$()
            .pipe(
                filter((error) => error != null),
                untilDestroyed(this),
            )
            .subscribe((error) => {
                this.tagError = error;
                this.fireErrorMessage();
                this.tagsProvider.resetTagsErrors();
            });
    }

    // ── from the pure view ─────────────────────────────────────────────────────────────────────

    onSave(form: IPersonForm): void {
        this.saving = true;
        const dto = this.toDto(form);
        if (this.isNew) {
            this.personsProvider.dispatchCreatePerson(dto);
        } else {
            this.personsProvider.dispatchUpdatePerson({ ...dto, _id: this.personId }, form.tasksUpdate);
        }
    }

    onCompanyClick(companyId: string): void {
        this.router.navigate([
            `/${this.localStorageService.getCurrentLanguage()}/${Paths.HOME}/${Paths.COMPANIES}`,
            companyId,
            Paths.PROFILE,
        ]);
    }

    onDelete(): void {
        this.personsProvider.dispatchDeletePerson(this.personId);
    }

    onStatusChange(active: boolean): void {
        this.personsProvider.dispatchUpdatePersonStatus(this.personId, active);
    }

    onTagCreate(name: string): void {
        this.tagsProvider.dispatchCreateTag(name);
    }

    /** The form asks, the backend counts; the answer flows back in through `tagUsage`. */
    onTagDeleteRequest(tagId: string): void {
        this.tagsProvider.dispatchTagUsage(tagId);
    }

    onTagDeleteConfirm(tagId: string): void {
        this.tagsProvider.dispatchDeleteTag(tagId);
    }

    onTagDeleteCancel(): void {
        this.tagsProvider.dispatchClearTagUsage();
    }

    // ── results ────────────────────────────────────────────────────────────────────────────────

    private listenToSaveAndDelete(): void {
        this.personsProvider
            .getSavePersonResult$()
            .pipe(untilDestroyed(this))
            .subscribe((result) => {
                this.saving = false;
                if (result.type === onCreatePersonSuccessfully.type) {
                    // Straight to the new person, like companies after creating.
                    const created = (result as { payload: IPersonDTO }).payload;
                    this.router.navigate([this.personsRoot, created._id, Paths.PROJECT]);
                    return;
                }
                // A failure is reported by `showErrors()` — the store carries it.
            });

        this.personsProvider
            .getDeletePersonResult$()
            .pipe(untilDestroyed(this))
            .subscribe((result) => {
                if (result.type === onDeletePersonSuccessfully.type) {
                    this.router.navigate([this.personsRoot, Paths.OVERVIEW]);
                }
            });
    }

    private get personsRoot(): string {
        return `/${this.localStorageService.getCurrentLanguage()}/${Paths.HOME}/${Paths.PERSONS}`;
    }

    // ── DTO ↔ form ─────────────────────────────────────────────────────────────────────────────

    private toForm(person: IPersonDTO): IPersonForm {
        return {
            firstName: person.firstName ?? '',
            lastName: person.lastName ?? '',
            assignee: person.assignee ?? null,
            assignedOn: person.assignedOn ? new Date(person.assignedOn) : null,
            clerk: person.clerk ?? null,
            assignedOnClerk: person.assignedOnClerk ? new Date(person.assignedOnClerk) : null,
            street: person.street ?? '',
            additionToAddress: person.additionToAddress ?? '',
            zip: person.zip ?? '',
            city: person.city ?? '',
            contacts: (person.contacts ?? []).map((contact) => ({ type: contact.type, value: contact.value })),
            companies: (person.companies ?? []).map((link) => ({
                companyId: link.companyId,
                companyName: link.companyName,
                role: link.role,
                companyDeleted: link.companyDeleted ?? false,
            })),
            tags: [...(person.tags ?? [])],
        };
    }

    /** `active` is never sent along: it only changes through the status endpoint. */
    private toDto(form: IPersonForm): IPersonDTO {
        return {
            firstName: form.firstName,
            lastName: form.lastName,
            assignee: form.assignee,
            assignedOn: form.assignedOn,
            clerk: form.clerk,
            assignedOnClerk: form.assignedOnClerk,
            street: form.street,
            additionToAddress: form.additionToAddress,
            zip: form.zip,
            city: form.city,
            contacts: form.contacts,
            // `companyName` / `companyDeleted` are set by the backend; sent along, it ignores them.
            companies: form.companies,
            tags: form.tags,
            active: this.active,
        };
    }
}
